import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  Skeleton,
} from "antd";
import ProjectSettingToggle from "./ProjectSettingToggle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchRFI, updateRFI } from "../services/rfisAPI";
import { fetchProjectUsers } from "../services/projectsAPI";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { toUTCMidnight, parseDateOnlyForPicker } from "../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { handleIntegerKeyDown, createIntegerChangeHandler } from "../utils/textUtils";
import AttachmentsSection from "./AttachmentsSection";
import { MinusCircleOutlined } from "@ant-design/icons";
import ProjectUserSelect from "./ProjectUserSelect";
import SpecSectionSelect from "./SpecSectionSelect";
import SimpleRichTextEditor from "./SimpleRichTextEditor";

const UpdateRFIModal = ({ visible, onClose, rfiId }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const isSequentialWorkflow = selectedProject?.rfi_workflow_type === "Sequential";
  const [ccSearchValue, setCcSearchValue] = useState("");
  const [assigneeSearchValue, setAssigneeSearchValue] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const statusValue = Form.useWatch("status", form);
  const emailNotificationsDisabled = statusValue !== "Open";

  const { data: projectUsers } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: ({ rfiId, rfiData, sendEmail }) => updateRFI(rfiId, rfiData, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries(["rfis", projectId]);
      onClose();
      form.resetFields();
      setCcSearchValue("");
      setAssigneeSearchValue("");
      setSendEmail(true); 
      notification.success({ message: "RFI updated successfully" });
    },
    onError: (error) => {
          const data = error.response?.data;
          const detail = data?.detail;
          const description = detail?.message || detail || error.message;
          notification.error({
            message: "Failed to update RFI",
            description,
          });
    },
  });

  const { data: rfi, refetch } = useQuery({
    queryKey: ["rfi", rfiId],
    queryFn: () => fetchRFI(rfiId),
    enabled: !!rfiId,
  });

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  const handleReceivedFromChange = (value) => {
    const receivedFrom = projectUsers?.data?.find(
      (user) => user.id === value || user._id === value,
    );
    if (receivedFrom?.vendor_id) {
      form.setFieldsValue({
        responsible_contractor_id: receivedFrom.vendor_id,
      });
    } else {
      // Clear the responsible contractor field if user has no vendor_id
      form.setFieldsValue({
        responsible_contractor_id: undefined,
      });
    }
  };

  const handleUpdateRFI = (values) => {
    const rfiData = {
      ...values,
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      attachments: values.attachments?.map((file) => file) || null,
    };

    // Handle Review Order for sequential workflow
    if (isSequentialWorkflow && values.steps && values.steps.length > 0) {
      rfiData.steps = values.steps.map((step) => step.user_ids || []);
      rfiData.assignee_ids = []; // Clear assignee_ids for sequential workflow
    } else {
      rfiData.steps = [];
      const assigneeIds = values.assignee_ids || [];
      const managerId = values.rfi_manager_id || rfi?.rfi_manager?._id;
      const currentBallInCourtIds = (rfi?.ball_in_court_ids || []).map((id) =>
        String(id),
      );
      const currentManagerId = rfi?.rfi_manager?._id;
      const managerCurrentlyHasBall =
        managerId &&
        currentBallInCourtIds.length === 1 &&
        currentBallInCourtIds[0] === String(currentManagerId);
      const ballInCourtIsEmpty = currentBallInCourtIds.length === 0;

      rfiData.assignee_ids = assigneeIds;
      rfiData.ball_in_court_ids = managerCurrentlyHasBall
        ? [managerId]
        : ballInCourtIsEmpty
          ? []
          : [...assigneeIds];
    }

    mutation.mutate({ rfiId: rfi.id, rfiData, sendEmail });
  };

  const handleClose = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, Close",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          form.resetFields();
          setCcSearchValue("");
          setAssigneeSearchValue("");
          setSendEmail(true); 
          onClose();
        },
      });
    } else {
      form.resetFields();
      setCcSearchValue("");
      setAssigneeSearchValue("");
      setSendEmail(true); 
      onClose();
    }
  };

  useEffect(() => {
    if (rfi) {
      // Handle Review Order for sequential workflow
      let steps = [];
      if (rfi.steps && rfi.steps.length > 0) {
        steps = rfi.steps.map((step) => ({
          user_ids: step.map((userId) =>
            typeof userId === "string" ? userId : userId.toString()
          ),
        }));
      }

      const formValues = {
        ...rfi,
        rfi_manager_id: rfi.rfi_manager._id,
        assignee_ids: rfi.assignees.map((assignee) => assignee._id),
        steps: steps,
        received_from_id: rfi.received_from ? rfi.received_from._id : undefined,
        responsible_contractor_id: rfi.responsible_contractor
          ? rfi.responsible_contractor.id
          : undefined,
        status: rfi.status,
        due_date: rfi.due_date ? parseDateOnlyForPicker(rfi.due_date) : null,
        attachments: rfi.attachments || [],
        spec_section_id: rfi.spec_section ? rfi.spec_section._id : undefined,
      };

      form.resetFields(); // Reset touched state first
      form.setFieldsValue(formValues); // Then set values
      const initialSendEmail = rfi.status === "Open";
      setSendEmail(initialSendEmail);
      form.setFieldsValue({ email_notifications: initialSendEmail });
    }
  }, [rfi, form]);

  useEffect(() => {
    if (emailNotificationsDisabled) {
      setSendEmail(false);
      form.setFieldsValue({ email_notifications: false });
    }
  }, [emailNotificationsDisabled, form]);

  return (
    <Modal
      title="Update RFI"
      visible={visible}
      onCancel={handleClose}
      footer={null}
    >
      {!rfi ? (
        <Skeleton active />
      ) : (
        <Form form={form} onFinish={handleUpdateRFI} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Number"
                name="number"
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Please enter only whole numbers!",
                  },
                ]}
              >
                <Input
                  type="number"
                  step="1"
                  onKeyDown={handleIntegerKeyDown}
                  onChange={createIntegerChangeHandler(form, "number")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Status" name="status">
                <Select>
                  <Select.Option value="Open">Open</Select.Option>
                  <Select.Option value="Closed">Closed</Select.Option>
                  <Select.Option value="Draft">Draft</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Title" name="title">
            <Input />
          </Form.Item>
          <Form.Item label="Question" name="question">
            <SimpleRichTextEditor
              placeholder="Enter your question..."
              aiEnhanceTarget="question"
            />
          </Form.Item>

          {!isSequentialWorkflow && (
            <Form.Item
              label="Assignees"
              name="assignee_ids"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error("Please select at least one assignee!")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <ProjectUserSelect
                searchValue={assigneeSearchValue}
                onSearch={setAssigneeSearchValue}
                placeholder="Select at least one assignee"
              />
            </Form.Item>
          )}

          {isSequentialWorkflow && (
            <Form.Item
              label="Review Order"
              name="steps"
              className="w-full"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error("Please add at least one Review Order!")
                      );
                    }
                    // Check that each step has at least one reviewer
                    for (let i = 0; i < value.length; i++) {
                      if (!value[i]?.user_ids || value[i].user_ids.length === 0) {
                        return Promise.reject(
                          new Error(
                            `Step #${i + 1} must have at least one reviewer!`
                          )
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Form.List name="steps">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey }, index) => (
                      <div key={key} className="mb-4 flex items-center">
                        <Form.Item
                          label={`Step #${index + 1}`}
                          name={[name, "user_ids"]}
                          fieldKey={[fieldKey, "user_ids"]}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (!value || value.length === 0) {
                                  return Promise.reject(
                                    new Error(
                                      "Please select at least one reviewer!"
                                    )
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                          className="w-full"
                        >
                          <ProjectUserSelect
                            placeholder="Select at least one reviewer"
                            className="flex-grow"
                          />
                        </Form.Item>
                        <Button
                          type="danger"
                          onClick={() => remove(name)}
                          icon={<MinusCircleOutlined />}
                          className="ml-2"
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      className="mb-4"
                    >
                      Add Review Step
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="RFI Manager" name="rfi_manager_id">
                <ProjectUserSelect
                  mode={null}
                  placeholder="Select RFI manager"
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Due Date" name="due_date">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Received From"
                name="received_from_id"
                getValueFromEvent={(value) => {
                  handleReceivedFromChange(value);
                  return value;
                }}
              >
                <ProjectUserSelect
                  mode={null}
                  allowClear
                  placeholder="Select received from"
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Specification Section"
                name="spec_section_id"
                className="w-full"
              >
                <SpecSectionSelect className="w-full" labelSeparator=" - " />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="responsible_contractor_id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Cc" name="distribution_list" className="w-full">
            <ProjectUserSelect
              searchValue={ccSearchValue}
              onSearch={setCcSearchValue}
              placeholder="Select Cc"
              className="w-full"
              allowClear
            />
          </Form.Item>

          <Form.Item name="attachments" label="Attachments" className="w-full">
            <AttachmentsSection projectId={projectId} />
          </Form.Item>

          <div className="project-settings-grid">
            <ProjectSettingToggle name="email_notifications" title="Email Notifications" description={emailNotificationsDisabled ? "Set status to Open to enable emails" : undefined} initialValue={true} checked={sendEmail} disabled={emailNotificationsDisabled} onChange={(checked) => {
                    setSendEmail(checked);
                    form.setFieldsValue({ email_notifications: checked });
            }} />
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={mutation.isPending}>
              {sendEmail ? "Update RFI and Send Emails" : "Update RFI"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UpdateRFIModal;
